import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faRotateRight, faPlus, faMinus, faArrowUp, faArrowDown, faArrowLeft, faArrowRight, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';

const AdvancedCropEditor = ({ imageUrl, rotation = 0, cropInfo = null, tileWidth = 300, tileHeight = 300, onSave, onCancel }) => {
    // Set initial state based on props
    const [currentRotation, setCurrentRotation] = useState(rotation);
    const [zoom, setZoom] = useState(0.8); // Changed default zoom from 1 to 0.5
    const [offsetX, setOffsetX] = useState(0); // Horizontal offset in pixels
    const [offsetY, setOffsetY] = useState(0); // Vertical offset in pixels
    const [imageLoaded, setImageLoaded] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [corsError, setCorsError] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [actualImageUrl, setActualImageUrl] = useState(null);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    // Use fixed tile dimensions based on props
    const tileDimensions = {
        width: tileWidth,
        height: tileHeight
    };
    
    // Create refs for different elements
    const imageRef = useRef(null);
    const canvasRef = useRef(null); // Main cropped preview
    const previewCanvasRef = useRef(null); // Mini preview of entire image with crop box
    
    // Define movement increment for arrow buttons (in pixels)
    const moveIncrement = 10;
    
    // Arrow button handlers
    const moveUp = () => setOffsetY(prev => prev - moveIncrement);
    const moveDown = () => setOffsetY(prev => prev + moveIncrement);
    const moveLeft = () => setOffsetX(prev => prev - moveIncrement);
    const moveRight = () => setOffsetX(prev => prev + moveIncrement);
    
    // Movement handlers for smaller increments
    const moveFineUp = () => setOffsetY(prev => prev - 1);
    const moveFineDown = () => setOffsetY(prev => prev + 1);
    const moveFineLeft = () => setOffsetX(prev => prev - 1);
    const moveFineRight = () => setOffsetX(prev => prev + 1);
    
    // Check if URL is a blob URL
    const isBlobUrl = (url) => {
        return url && typeof url === 'string' && url.startsWith('blob:');
    };
    
    // Check if URL is a data URL
    const isDataUrl = (url) => {
        return url && typeof url === 'string' && url.startsWith('data:');
    };
    
    // Set up the image URL, handling different URL types
    useEffect(() => {
        // Add a safety check to handle undefined or null imageUrl
        if (!imageUrl) {
            console.warn("No image URL provided or URL is undefined/null");
            setImageError(true);
            setCorsError(true);
            setImageLoaded(true); // Set to true to enable controls
            // Use fallback placeholder
            setActualImageUrl('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iMTIiIHk9IjEyIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZpbGw9IiM5OTk5OTkiPj88L3RleHQ+PC9zdmc+');
            return;
        }

        console.log("Setting up image URL for editing:", 
            imageUrl.substring(0, 50) + (imageUrl.length > 50 ? '...' : ''));
        
        // Check for different URL types and handle accordingly
        if (imageUrl.startsWith('data:')) {
            setActualImageUrl(imageUrl);
            setCorsError(false);
        }
        else if (imageUrl.includes("placeholder_") || imageUrl.includes("tile_") || imageUrl.includes("tile.jpeg")) {
            console.log("Detected processed/placeholder image.");
            setCorsError(true);
            setImageLoaded(true);
            setActualImageUrl(imageUrl);
        }
        else if (isBlobUrl(imageUrl)) {
            setActualImageUrl(imageUrl);
        }
        else {
            // Regular URL (http/https)
            setActualImageUrl(imageUrl);
        }
    }, [imageUrl]);
    
    // Initialize crop info if provided
    useEffect(() => {
        if (cropInfo && imageLoaded && imageDimensions.width > 0) {
            // Convert percentage crop info to pixel offset
            const imgWidth = imageDimensions.width;
            const imgHeight = imageDimensions.height;
            
            // Calculate crop center position
            const cropCenterX = (cropInfo.x + cropInfo.width / 2) / 100 * imgWidth - imgWidth / 2;
            const cropCenterY = (cropInfo.y + cropInfo.height / 2) / 100 * imgHeight - imgHeight / 2;
            
            // Convert to offsets
            setOffsetX(cropCenterX);
            setOffsetY(cropCenterY);
            
            // Calculate zoom based on crop size
            const widthZoom = (imgWidth * cropInfo.width / 100) / tileDimensions.width;
            const heightZoom = (imgHeight * cropInfo.height / 100) / tileDimensions.height;
            
            // Use the smaller zoom to ensure the crop fits within the image
            setZoom(1 / Math.max(widthZoom, heightZoom));
        }
    }, [cropInfo, imageLoaded, imageDimensions, tileDimensions.width, tileDimensions.height]);
    
    // Handle image load success
    const handleImageLoad = () => {
        console.log("Image loaded successfully");
        setImageLoaded(true);
        setImageError(false);
        
        // Store image dimensions
        if (imageRef.current) {
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight
            });
        }
        
        if (isDataUrl(actualImageUrl)) {
            setCorsError(false);
        } else {
            // Check if we can draw to canvas
            try {
                const testCanvas = document.createElement('canvas');
                testCanvas.width = 10;
                testCanvas.height = 10;
                const ctx = testCanvas.getContext('2d');
                ctx.drawImage(imageRef.current, 0, 0, 10, 10);
                setCorsError(false);
            } catch (err) {
                setCorsError(true);
            }
        }
    };
    
    // Handle image load error
    const handleImageError = () => {
        console.error("Error loading image");
        setImageError(true);
        setImageLoaded(true);
        setCorsError(true);
        setActualImageUrl('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iMTIiIHk9IjEyIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZpbGw9IiM5OTk5OTkiPj88L3RleHQ+PC9zdmc+');
    };
    
    // Crop and draw the tile from the image (main preview)
    const cropTileFromImage = (img) => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        
        // Set canvas dimensions to match tile dimensions
        canvas.width = tileDimensions.width;
        canvas.height = tileDimensions.height;
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        try {
            // Create a temporary canvas for the rotated image
            const tempCanvas = document.createElement('canvas');
            const maxDimension = Math.max(img.naturalWidth, img.naturalHeight) * 2;
            tempCanvas.width = maxDimension;
            tempCanvas.height = maxDimension;
            const tempCtx = tempCanvas.getContext('2d');
            
            // Clear the temporary canvas
            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            // Calculate center points
            const imgCenterX = img.naturalWidth / 2;
            const imgCenterY = img.naturalHeight / 2;
            const tempCenterX = tempCanvas.width / 2;
            const tempCenterY = tempCanvas.height / 2;
            
            // Draw the original image centered on temp canvas
            tempCtx.drawImage(
                img, 
                tempCenterX - imgCenterX, 
                tempCenterY - imgCenterY
            );
            
            // Create another canvas for the rotated version
            const rotatedCanvas = document.createElement('canvas');
            rotatedCanvas.width = tempCanvas.width;
            rotatedCanvas.height = tempCanvas.height;
            const rotatedCtx = rotatedCanvas.getContext('2d');
            
            // Apply rotation around the center
            rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
            rotatedCtx.rotate((currentRotation * Math.PI) / 180);
            rotatedCtx.translate(-rotatedCanvas.width / 2, -rotatedCanvas.height / 2);
            
            // Draw the original image onto the rotated canvas
            rotatedCtx.drawImage(tempCanvas, 0, 0);
            
            // Calculate the region to crop based on zoom and offset
            // These offsets are in the original unrotated coordinate space
            const scaledWidth = tileDimensions.width / zoom;
            const scaledHeight = tileDimensions.height / zoom;
            
            // We need to transform the offsets to account for rotation
            // First get the center in the original coordinate space
            const originalCenterX = imgCenterX + offsetX;
            const originalCenterY = imgCenterY + offsetY;
            
            // Now transform this point based on rotation
            // For simplicity when dealing with the rotated canvas, we need to 
            // convert from image-relative coordinates to rotated canvas coordinates
            const rotatedCenterX = tempCenterX + offsetX;
            const rotatedCenterY = tempCenterY + offsetY;
            
            // Calculate the crop box position in the rotated space
            const cropX = rotatedCenterX - scaledWidth / 2;
            const cropY = rotatedCenterY - scaledHeight / 2;
            
            // Finally, draw the cropped region to the main display canvas
            ctx.drawImage(
                rotatedCanvas,
                cropX,
                cropY,
                scaledWidth,
                scaledHeight,
                0,
                0,
                tileDimensions.width,
                tileDimensions.height
            );
        } catch (error) {
            console.error("Error in cropTileFromImage:", error);
            
            // Fallback: display error on canvas
            ctx.fillStyle = '#FF0000';
            ctx.font = '14px Arial';
            ctx.fillText('Error rendering preview', 10, 30);
        }
    };
    
    // Draw mini preview with red rectangle for crop area
    const drawMiniPreview = (img) => {
        const canvas = previewCanvasRef.current;
        if (!canvas || !img) return;
        
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        
        // Set sizes for the preview canvas
        const previewSize = 200;
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        canvas.width = previewSize;
        canvas.height = previewSize / aspectRatio;
        
        // Calculate scale factor from original image to preview
        const scaleX = canvas.width / img.naturalWidth;
        const scaleY = canvas.height / img.naturalHeight;
        const scale = Math.min(scaleX, scaleY);
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw the image with rotation
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((currentRotation * Math.PI) / 180);
        ctx.drawImage(
            img, 
            -img.naturalWidth * scale / 2, 
            -img.naturalHeight * scale / 2, 
            img.naturalWidth * scale, 
            img.naturalHeight * scale
        );
        ctx.restore();
        
        // Calculate the crop rectangle position and size
        const tileAspectRatio = tileDimensions.width / tileDimensions.height;
        
        // Calculate crop box dimensions in preview scale
        let cropW = (tileDimensions.width / zoom) * scale;
        let cropH = (tileDimensions.height / zoom) * scale;
        
        // Calculate the center position of the crop box
        const cropCenterX = canvas.width / 2 + (offsetX * scale);
        const cropCenterY = canvas.height / 2 + (offsetY * scale);
        
        // For rotated rectangles at 90° or 270°, swap width and height if not square
        if ((Math.abs(currentRotation) % 180 === 90) && tileAspectRatio !== 1) {
            [cropW, cropH] = [cropH, cropW];
        }
        
        // Draw the crop rectangle on top of the rotated image
        ctx.save();
        ctx.translate(cropCenterX, cropCenterY);
        ctx.rotate((currentRotation * Math.PI) / 180);
        ctx.strokeStyle = "#FF0000";
        ctx.lineWidth = 2;
        ctx.strokeRect(-cropW / 2, -cropH / 2, cropW, cropH);
        ctx.restore();
    };
    
    // Update the previews when parameters change
    useEffect(() => {
        if (imageLoaded && imageRef.current) {
            cropTileFromImage(imageRef.current);
            drawMiniPreview(imageRef.current);
        }
    }, [imageLoaded, offsetX, offsetY, zoom, currentRotation, tileDimensions.width, tileDimensions.height]);
    
    // Handle rotation buttons
    const rotateLeft = () => setCurrentRotation((prev) => (prev - 90) % 360);
    const rotateRight = () => setCurrentRotation((prev) => (prev + 90) % 360);
    
    // Convert the crop settings to percentage values for saving
    const handleSave = () => {
        if (!imageLoaded || !imageRef.current) {
            console.error("Cannot save: image not properly loaded");
            onCancel();
            return;
        }
        
        const img = imageRef.current;
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
        
        // Calculate final crop dimensions and position based on the current rotation
        // Using fixed tileDimensions.width and tileDimensions.height
        const scaledWidth = tileDimensions.width / zoom;
        const scaledHeight = tileDimensions.height / zoom;
        const centerX = imgWidth / 2 + offsetX;
        const centerY = imgHeight / 2 + offsetY;
        
        // If rotation is 90 or 270 degrees and the tile is not square,
        // we need to swap width and height for the crop calculation
        let cropWidth = scaledWidth;
        let cropHeight = scaledHeight;
        
        if ((Math.abs(currentRotation % 180) === 90) && tileDimensions.width !== tileDimensions.height) {
            // Swap dimensions for rectangular tiles at 90/270 degrees
            [cropWidth, cropHeight] = [cropHeight, cropWidth];
        }
        
        // Calculate percentage values for crop
        const cropX = ((centerX - cropWidth / 2) / imgWidth) * 100;
        const cropY = ((centerY - cropHeight / 2) / imgHeight) * 100;
        const cropWidthPercent = (cropWidth / imgWidth) * 100;
        const cropHeightPercent = (cropHeight / imgHeight) * 100;
        
        // Normalize crop values (ensure they are within 0-100 range)
        const normalizedCropInfo = {
            x: Math.max(0, Math.min(100, cropX)),
            y: Math.max(0, Math.min(100, cropY)),
            width: Math.min(100, cropWidthPercent),
            height: Math.min(100, cropHeightPercent)
        };
        
        console.log("Saving crop settings:", {
            rotation: currentRotation,
            crop: normalizedCropInfo
        });
        
        // Create a preview image from the canvas
        let preview = null;
        try {
            preview = canvasRef.current.toDataURL('image/jpeg', 0.9);
        } catch (error) {
            console.error("Canvas toDataURL error:", error);
        }
        
        // Save with the calculated crop info
        onSave(currentRotation, normalizedCropInfo, preview);
    };
    
    // Add keyboard event handlers
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch(e.key) {
                case 'ArrowUp':
                    e.shiftKey ? moveFineUp() : moveUp();
                    break;
                case 'ArrowDown':
                    e.shiftKey ? moveFineDown() : moveDown();
                    break;
                case 'ArrowLeft':
                    e.shiftKey ? moveFineLeft() : moveLeft();
                    break;
                case 'ArrowRight':
                    e.shiftKey ? moveFineRight() : moveRight();
                    break;
                case '+':
                case '=':
                    setZoom(prev => Math.min(prev + 0.1, 2.0));
                    break;
                case '-':
                    setZoom(prev => Math.max(prev - 0.1, 0.1));
                    break;
                case 'r':
                    rotateRight();
                    break;
                case 'l':
                    rotateLeft();
                    break;
                case 'Escape':
                    onCancel();
                    break;
                case 'Enter':
                    if (e.ctrlKey || e.metaKey) handleSave();
                    break;
                default:
                    return;
            }
            e.preventDefault();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-auto">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Adjust T-shirt Crop</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="flex flex-col md:flex-row flex-1 p-4 gap-6">
                    <div className="flex flex-col items-center w-full md:w-2/3 gap-4">
                        {/* Main Canvas - Cropped Preview */}
                        <div className="w-full flex flex-col items-center">
                            <h3 className="text-lg font-semibold mb-2">Center Tile Preview:</h3>
                            {imageLoaded ? (
                                <canvas 
                                    ref={canvasRef} 
                                    className="border border-gray-400" 
                                    style={{ width: tileDimensions.width, height: tileDimensions.height }} 
                                />
                            ) : (
                                <div className="border border-gray-400 flex items-center justify-center" 
                                     style={{ width: tileDimensions.width, height: tileDimensions.height }}>
                                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                        
                        {/* Mini Preview with Crop Rectangle */}
                        <div className="mt-4 w-full flex flex-col items-center">
                            <h3 className="text-md font-medium mb-2">Mini Preview of Entire Image</h3>
                            <canvas ref={previewCanvasRef} className="border border-blue-300 rounded max-w-full" />
                        </div>
                        
                        {/* Hidden Image Reference */}
                        {actualImageUrl && (
                            <img 
                                ref={imageRef}
                                src={actualImageUrl} 
                                alt="Reference"
                                className="hidden"
                                crossOrigin="anonymous"
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                            />
                        )}
                    </div>
                    
                    <div className="w-full md:w-1/3 flex flex-col gap-4">
                        {/* Zoom Control */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Zoom Level: {zoom.toFixed(1)}x</label>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.1))}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full h-8 w-8 flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faMinus} />
                                    </button>
                                    <button 
                                        onClick={() => setZoom(prev => Math.min(prev + 0.1, 2.0))}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full h-8 w-8 flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </div>
                            <input 
                                type="range" 
                                min="0.1" 
                                max="2.0" 
                                step="0.1" 
                                value={zoom} 
                                onChange={(e) => setZoom(parseFloat(e.target.value))} 
                                className="w-full" 
                            />
                        </div>
                        
                        {/* Position Controls with Arrow Buttons */}
                        <div className="mt-2">
                            <label className="text-sm font-medium mb-2 block">Position Control</label>
                            
                            {/* Arrow buttons for position control */}
                            <div className="flex flex-col items-center gap-1">
                                {/* Top arrow */}
                                <button 
                                    onClick={moveUp}
                                    className="bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg h-10 w-10 flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                
                                {/* Middle row with left/right arrows */}
                                <div className="flex gap-1">
                                    <button 
                                        onClick={moveLeft}
                                        className="bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg h-10 w-10 flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                    </button>
                                    
                                    <div className="bg-gray-200 h-10 w-10 flex items-center justify-center rounded-lg text-xs text-gray-500">
                                        Move
                                    </div>
                                    
                                    <button 
                                        onClick={moveRight}
                                        className="bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg h-10 w-10 flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </button>
                                </div>
                                
                                {/* Bottom arrow */}
                                <button 
                                    onClick={moveDown}
                                    className="bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg h-10 w-10 flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                            </div>
                            
                            <div className="text-xs text-center mt-1 text-gray-500">
                                Use arrow keys for movement (hold Shift for fine control)
                            </div>
                        </div>
                        
                        {/* Rotation Controls */}
                        <div className="flex flex-col gap-2 mt-2">
                            <div className="flex items-center gap-2">
                                <label className="text-sm">Rotation</label>
                                <span className="text-sm ml-auto">{currentRotation}°</span>
                            </div>
                            <input 
                                type="range" 
                                min="0" 
                                max="360" 
                                value={currentRotation} 
                                onChange={(e) => setCurrentRotation(parseInt(e.target.value))} 
                                className="w-full" 
                            />
                            <div className="flex gap-2 mt-1">
                                <button 
                                    onClick={rotateLeft} 
                                    className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 p-2 rounded"
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
                                    -90°
                                </button>
                                <button 
                                    onClick={rotateRight} 
                                    className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 p-2 rounded"
                                >
                                    <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
                                    +90°
                                </button>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 mt-4">
                            <button 
                                onClick={handleSave} 
                                className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-md font-medium"
                            >
                                <FontAwesomeIcon icon={faSave} className="mr-2" />
                                Save & Process
                            </button>
                            
                            <button 
                                onClick={onCancel} 
                                className="border border-gray-300 hover:bg-gray-100 text-gray-700 p-3 rounded-md font-medium"
                            >
                                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                Cancel
                            </button>
                        </div>
                        
                        {/* Keyboard shortcuts info */}
                        <div className="mt-2 p-2 bg-gray-100 border border-gray-300 rounded text-xs text-gray-600">
                            <p>Keyboard shortcuts:</p>
                            <ul className="list-disc ml-4 mt-1">
                                <li>Arrow keys to move</li>
                                <li>Shift + Arrow for precision</li>
                                <li>+/- to zoom in/out</li>
                                <li>R to rotate right</li>
                                <li>L to rotate left</li>
                                <li>Esc to cancel</li>
                                <li>Ctrl+Enter to save</li>
                            </ul>
                        </div>
                        
                        {/* Error Messages */}
                        {(corsError || imageError) && (
                            <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded text-sm">
                                Note: Preview mode only - actual crop will be applied on save.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedCropEditor;
